import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { Card, Alert, CardBody, CardFooter } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { Button, PageTitle, Field, useSession, Form } from '@cordata/buffet';

import { sessionInstance } from '~/utils';
import gateKeep from '~/utils/gate-keeper';

const ChangePassword = () => {
  const history = useHistory();
  const {
    current, fetchAppUser, changePassword,
  } = useSession();
  const [passwordResetRequired, setPasswordResetRequired] = useState(false);
  const [passwordExpiresIn, setPasswordExpiresIn] = useState();
  const [isPasswordExpired, setIsPasswordExpired] = useState(passwordExpiresIn <= 0);
  const [mobileNumber, setMobileNumber] = useState();
  const [passwordPolicy, setPasswordPolicy] = useState(Yup.string().required());
  const [passwordPolicyHelpText, setPasswordPolicyHelpText] = useState([]);
  const [status, setStatus] = useState('loading');
  const [hasAppKickstarter, setKs] = useState(false);

  useEffect(() => {
    setStatus('loading');

    if (current) {
      const p = current.attributes.permissions.map(permission => permission.securityPermissionName);

      setKs(p.includes('hasAppKickstarter'));

      fetchAppUser(current)
        .then(() => {
          setPasswordResetRequired(current &&
            current.attributes.passwordResetRequired);
          setPasswordExpiresIn(current &&
            current.attributes.passwordExpiresIn);

          setStatus('success');
        })
        .catch((e) => {
          if (e.response.status === 403) {
            setPasswordResetRequired(true);
            setStatus('success');
          }
        });
    }
  }, [current]);

  useEffect(() => {
    setIsPasswordExpired(passwordResetRequired || passwordExpiresIn <= 0);
  }, [passwordExpiresIn, passwordResetRequired]);

  useEffect(() => {
    sessionInstance
      .get('/mobile-phone')
      .then(r => r.data.data)
      .then(r => setMobileNumber(r.attributes.phoneNumber));

    sessionInstance
      .get('/password-policy')
      .then(r => r.data)
      .then((r) => {
        let newPolicy = passwordPolicy;
        const newPolicyHelpText = passwordPolicyHelpText;

        if (r.passwordMinLength.value) {
          newPolicy = newPolicy.min(r.passwordMinLength.value);
          newPolicyHelpText.push(`${r.passwordMinLength.value} characters`);
        }

        if (r.passwordRequiresLowercase.value) {
          newPolicy = newPolicy.matches(/^.*[a-z].*$/, 'must contain a lowercase character');
          newPolicyHelpText.push('a lowercase character');
        }

        if (r.passwordRequiresUppercase.value) {
          newPolicy = newPolicy.matches(/^.*[A-Z].*$/, 'must contain an uppercase character');
          newPolicyHelpText.push('an uppercase character');
        }

        if (r.passwordRequiresNumber.value) {
          newPolicy = newPolicy.matches(/^.*\d.*$/, 'must contain a number');
          newPolicyHelpText.push('a number');
        }

        if (r.passwordRequiresSpecial.value) {
          newPolicy = newPolicy.matches(/^.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~].*$/, 'must contain a symbol');
          newPolicyHelpText.push('a symbol');
        }

        setPasswordPolicy(newPolicy);
        setPasswordPolicyHelpText(newPolicyHelpText);
      });
  }, []);

  const handleCancel = () => {
    history.push('/logout');
  };

  const handleContinue = () => {
    if (hasAppKickstarter) {
      history.push('/home', { skipPasswordCheck: true });
    } else {
      sessionInstance
        .get('/destinations')
        .then(({ data: { data: r } }) => {
          gateKeep(r, null, current.attributes.permissions);
        });
    }
  };

  return (
    <>
      <PageTitle>Change Password</PageTitle>

      <Card>
        <Form
          displayName="ChangePasswordForm"
          enableReinitialize

          initialValues={{
            oldPassword: '',
            newPassword: '',
            newPasswordConfirm: '',
            mobileNumber,
          }}

          validationSchema={Yup.object().shape({
            oldPassword: Yup.string().required(),
            newPassword: passwordPolicy,
            newPasswordConfirm: Yup.string()
              .oneOf([Yup.ref('newPassword'), null], "doesn't match")
              .required(),
          })}

          onSubmit={(values, { setErrors, setSubmitting }) => {
            changePassword({
              'current-password': values.oldPassword,
              'new-password': values.newPassword,
              'mobile-phone':
                values.mobileNumber && values.mobileNumber !== ''
                  ? values.mobileNumber.replace(/\D/g, '')
                  : null,
            })
              .then(() => history.push('/home'))
              .catch(({ response: { data: { errors } } }) => {
                if (errors) {
                  setErrors({
                    server: errors,
                    oldPassword: errors.currentPassword,
                    newPassword: errors.newPassword,
                  });
                }
              })
              .then(() => setSubmitting(false));
          }}
        >
          {({
            isSubmitting,
          }) => (
            <>
              <CardBody>
                {status === 'success' &&
                  isPasswordExpired && (
                    <Alert color="danger">
                      <h5 className="alert-heading">
                        Your password <strong>has expired</strong>.
                      </h5>

                      <p className="mb-0">
                        In order to continue using Cordata{' '}
                        <strong>you must change your password</strong>.
                      </p>
                    </Alert>
                )}

                {status === 'success' &&
                  !passwordResetRequired &&
                  passwordExpiresIn <= 7 &&
                  passwordExpiresIn > 0 && (
                    <Alert color="warning">
                      Your password will expire in{' '}
                      <strong>{passwordExpiresIn}</strong> day
                      {passwordExpiresIn === 1 ? '' : 's'}.
                    </Alert>
                )}

                <Field
                  name="oldPassword"
                  label="Your old password"
                  type="password"
                  required
                />

                <Field
                  name="newPassword"
                  label="Your new password"
                  type="password"
                  text={`must contain: ${passwordPolicyHelpText.join(', ')}`}
                  required
                />

                <Field
                  name="newPasswordConfirm"
                  label="Your new password again"
                  type="password"
                  required
                />

                <Field
                  name="mobileNumber"
                  type="tel"
                  text="Your mobile number can be used to unlock your account if you forget your password. This is the current mobile number we have on file. If you would like to update it now, please enter your mobile number."
                />
              </CardBody>

              <CardFooter className="d-flex">
                <div className="ml-auto">
                  {isPasswordExpired ? (
                    <Button
                      type="button"
                      color="tertiary"
                      outline
                      disabled={isSubmitting}
                      onClick={handleCancel}
                    >
                    Cancel
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      color="tertiary"
                      outline
                      disabled={isSubmitting}
                      onClick={handleContinue}
                    >
                    Continue
                    </Button>
                  )}

                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Changing Password ...' : 'Change Password'}
                  </Button>
                </div>
              </CardFooter>
            </>
          )}
        </Form>
      </Card>
    </>
  );
};

export default ChangePassword;
